import intlTelInput from 'intl-tel-input';

declare var jQuery: any;
declare var vm_phone_input: { utils_script_url?: string; invalid_message?: string };

const PHONE_SELECTOR_GLOBAL = 'input[type="tel"], input.js-phoneInput';
const PHONE_SELECTOR_SCOPED = '.vm-address-fixed-fields input[type="tel"], .vm-address-fixed-fields input.js-phoneInput';

function getUtilsScript(): string {
    if (typeof vm_phone_input !== 'undefined' && vm_phone_input.utils_script_url) {
        return vm_phone_input.utils_script_url;
    }
    return 'utils.js';
}

function getInvalidMessage(): string {
    if (typeof vm_phone_input !== 'undefined' && vm_phone_input.invalid_message) {
        return vm_phone_input.invalid_message;
    }
    return 'Invalid phone';
}

/** Base field id (the hidden mirror input) for a visible tel input. */
function getPhoneBaseId(telInput: any): string {
    return (jQuery(telInput).attr('id') || '').replace(/_tel_input$/, '');
}

/**
 * Flag a phone field as invalid: red border, `input-wrong` class and a visible
 * message. Reuses a form-rendered `#<id>-error` element when present, otherwise
 * injects one after the intl-tel-input wrapper so the message shows even for
 * fields (e.g. AddressFixedFields) that don't render an error element.
 */
function showPhoneError(telInput: any, message: string): void {
    const $tel = jQuery(telInput);
    $tel.addClass('input-wrong').css('border', '1px solid #e2401c');

    const baseId = getPhoneBaseId(telInput);
    let $error = baseId ? jQuery('#' + baseId + '-error') : jQuery();
    if (!$error.length) {
        const $anchor = $tel.closest('.iti').length ? $tel.closest('.iti') : $tel;
        $error = $anchor.next('.vm-phone-error');
        if (!$error.length) {
            $error = jQuery('<span class="vm-phone-error" style="display:block;color:#e2401c;font-size:0.85em;margin-top:4px;"></span>');
            $anchor.after($error);
        }
    }
    $error.text(message).show();
}

/** Clear any invalid state/message previously set by showPhoneError. */
function clearPhoneError(telInput: any): void {
    const $tel = jQuery(telInput);
    $tel.removeClass('input-wrong').css('border', '');

    const baseId = getPhoneBaseId(telInput);
    if (baseId) {
        jQuery('#' + baseId + '-error').text('').hide();
    }
    const $anchor = $tel.closest('.iti').length ? $tel.closest('.iti') : $tel;
    $anchor.next('.vm-phone-error').text('').hide();
}

function convertPhoneFormat(elem: any, currValue: string, placeholder: string) {
    let i = 0;
    let j = 0;
    let newValstring = '';
    for (i = 0; i < placeholder.length; i++) {
        if (j >= currValue.length) {
            break;
        }
        if (isNaN(parseInt(placeholder[i], 10))) {
            newValstring += placeholder[i];
        } else {
            newValstring += currValue[j];
            j++;
        }
    }
    elem.val(newValstring);
}

function updatePhoneInput(e: any): boolean {
    const charCode = e.which ? e.which : e.keyCode;
    if (String.fromCharCode(charCode).match(/[^0-9.]/g)) {
        return false;
    }

    const placeholder: string = jQuery(e.target).attr('placeholder') || '';
    const currValue: string = jQuery(e.target).val().replace(/[^0-9]/g, '');
    if (e.target.selectionStart === e.target.selectionEnd) {
        if (placeholder.replace(/[^0-9]/g, '').length <= currValue.length) {
            return false;
        }
    }
    convertPhoneFormat(jQuery(e.target), currValue, placeholder);
    return true;
}

function resolveScope(scope?: any): JQuery {
    if (!scope) {
        return jQuery(document);
    }
    if (typeof scope === 'string') {
        return jQuery(scope);
    }
    return jQuery(scope);
}

function isAlreadyInitialized(input: HTMLElement): boolean {
    return input.hasAttribute('data-intl-tel-input-id')
        || jQuery(input).closest('.iti').length > 0;
}

/**
 * Initialize intl-tel-input on tel fields (skips already-initialized inputs).
 */
export function initPhoneInputs(scope?: any): void {
    const $scope = resolveScope(scope);
    const selector = scope ? PHONE_SELECTOR_SCOPED : PHONE_SELECTOR_GLOBAL;
    $scope.find(selector).each((_: number, rawInput: any) => {
        const input: HTMLInputElement = rawInput;
        if (!input || !input.id || isAlreadyInitialized(input)) {
            return;
        }

        const inputId: string = input.id;
        let classesInput = '';
        if (jQuery(input).hasClass('login_input')) {
            classesInput = "class='login_input js-phoneInput'";
        }

        input.id = inputId + '_tel_input';
        input.name = input.name + '_tel_input';

        jQuery(input).after(
            "<input type='hidden' " + classesInput + " id='" + inputId + "' name='" + inputId + "' value='" + jQuery(input).val() + "'/>"
        );

        const $addressBlock = jQuery(input).closest('.vm-address-fixed-fields');
        const $countrySelect = $addressBlock.find('select[id$="_country"], select.vm-address-fixed-country').first();
        const initialCountry = ($countrySelect.length && $countrySelect.val())
            ? String($countrySelect.val()).toLowerCase()
            : 'ca';

        const inputPhone: any = intlTelInput(input, {
            initialCountry,
            autoPlaceholder: 'aggressive',
            utilsScript: getUtilsScript(),
        });

        const invalidMessage = getInvalidMessage();

        jQuery(input).on('keypress.vmPhone', (e: any) => updatePhoneInput(e));
        jQuery(input).on('blur.vmPhone', (e: any) => {
            jQuery('#' + inputId).val(inputPhone.getNumber());
            if (jQuery(e.target).val() !== '' && !inputPhone.isValidNumber()) {
                showPhoneError(e.target, invalidMessage);
            } else {
                clearPhoneError(e.target);
            }
        });

        if (inputPhone.countryList) {
            jQuery(inputPhone.countryList).find('li').on('click.vmPhone', () => {
                jQuery(inputPhone.telInput).val('');
            });
        }

        const placeholder: string = jQuery(input).attr('placeholder') || '';
        const currValue: string = jQuery(input).val().replace(/[^0-9]/g, '');
        convertPhoneFormat(jQuery(input), currValue, placeholder);
    });
}

/**
 * Validate tel fields in scope; sync hidden values. Returns false if any non-empty phone is invalid.
 */
export function validatePhoneInputs(scope?: any): boolean {
    const itiGlobals = (window as any).intlTelInputGlobals;
    const $scope = resolveScope(scope);
    let valid = true;
    const phoneError = getInvalidMessage();

    $scope.find('.vm-address-fixed-fields input[type="tel"], .vm-address-fixed-fields input.js-phoneInput').each((_: number, telInput: any) => {
        const $tel = jQuery(telInput);
        const rawValue = String($tel.val() || '').trim();

        if (!rawValue.length) {
            clearPhoneError(telInput);
            return;
        }

        const instance = itiGlobals?.getInstance ? itiGlobals.getInstance(telInput) : null;
        const hiddenId = getPhoneBaseId(telInput);

        if (hiddenId && instance) {
            jQuery('#' + hiddenId).val(instance.getNumber());
        }

        const numberValid = instance ? instance.isValidNumber() : false;
        if (!numberValid) {
            valid = false;
            showPhoneError(telInput, phoneError);
        } else {
            clearPhoneError(telInput);
        }
    });

    return valid;
}

export function registerPhoneInputListeners(): void {
    jQuery(document.body).on('vm_address_fixed_sections_visible', () => {
        initPhoneInputs('.woocommerce-checkout, #checkout-sections-container, .vm-address-fixed-fields');
    });

    jQuery(document).ready(() => {
        if (jQuery('form.checkout, .woocommerce-checkout, form.edit-account, .vm-address-fixed-fields').length) {
            initPhoneInputs();
        }
    });
}

(window as any).VmPhoneInput = {
    init: initPhoneInputs,
    validate: validatePhoneInputs,
};
